/**
 * Browser bootstrap. Kept tiny and separate from app.js so app.js stays
 * testable with an injected document (jsdom) and this file is the only piece
 * that assumes a real browser.
 */
import { startApp } from './app.js';
import { deriveSaveSyncIndicator } from './save-sync-status.js';
import { wireAccountPanel } from './account.js';
import { createAuth } from '../sync/auth.js';
import { getSupabase } from '../sync/supabase-client.js';
import { SUPABASE_URL } from '../sync/supabase-config.js';
import { createSyncEngine } from '../sync/sync-engine.js';
import { deriveProjectId } from '../sync/ids.js';
import { rowsToSketch, sketchToRows } from '../sync/mapping.js';
import { pullRemeasures, pullReusableSections, pullRooms, pushMeasurementSession, pushRemeasure, pushReusableSection, pushRoom, pushRoomDelete, pullMeasurementSessions } from '../sync/sync-client.js';
import { remeasureSessionToRows, rowToRemeasureSession } from '../measurement/remeasure-session-mapping.js';
import { measurementSessionToRows, rowToMeasurementSession } from '../measurement/measurement-session-mapping.js';
import { componentToRow, rowToComponent } from '../sync/reusable-section-mapping.js';
import { createReusableSectionLibrary } from '../store/reusable-sections.js';
import { cachedRuleRows, pullBusinessRules } from '../sync/rules.js';
import {
  deleteMeasurementSession, deleteSketch, getMeasurementSession, getMeta, getMetaResult,
  getRemeasureSession, getSketch, listMeasurementSessions, listRemeasureSessions,
  listSketches, putMeasurementSession, putRemeasureSession, putSketch, setMeta,
} from '../store/local-db.js';

// Assigned after startApp finishes so the sync callback can notify the live
// sketch when a remote pull lands. ES modules are strict mode, so this must be
// declared explicitly rather than relying on an accidental window global.
let app = null;

let localSaveState = 'saved';
let latestSyncStatus = { state: 'signed-out', pending: 0, message: '' };
let serverDirty = false;

/** Paints the compact yellow-local / green-server persistence indicator. */
function renderSaveSyncIndicator() {
  const el = document.getElementById('sync-status');
  const label = document.getElementById('sync-status-label');
  if (!el || !label) return;
  const indicator = deriveSaveSyncIndicator({
    localState: localSaveState,
    syncState: latestSyncStatus.state,
    pending: latestSyncStatus.pending,
    serverDirty,
    message: latestSyncStatus.message,
    rulesError: el.dataset.rulesError ?? '',
  });
  label.textContent = indicator.label;
  el.title = indicator.label;
  el.className = `sync-status sync-status--${indicator.tone}`;
}

/** Marks a new local edit yellow until the server confirms its sync. */
function renderLocalSaveStatus(state) {
  localSaveState = state;
  if (state === 'saving' || state === 'saved') serverDirty = true;
  renderSaveSyncIndicator();
}

/** Reflects cloud state without conflating it with the durable local save. */
function renderSyncStatus(status) {
  latestSyncStatus = status;
  if (status.state === 'synced' && Number(status.pending) === 0) serverDirty = false;
  renderSaveSyncIndicator();
}

async function boot() {
  // Offline-first remains the local source of truth. Authentication enables
  // the existing Supabase push/pull backup so the same account can work across
  // devices; signed-out sketching still stays available locally.
  const auth = createAuth(getSupabase());
  // Start immediately from the locally cached rule rows. A later signed-in
  // refresh may update that cache, but the app itself must never wait on (or
  // reference an undeclared value from) the cloud request.
  let ruleRows = {};
  try {
    // Some tablet browsers can leave an IndexedDB open request pending forever
    // (neither success, error, nor blocked fires). Do not let that freeze every
    // toolbar listener before startApp() gets a chance to run. Cached admin
    // rules enhance the compiled offline defaults, so give the cache a brief
    // opportunity and then start safely without it.
    const cacheTimedOut = Symbol('cache-timed-out');
    let timeoutId;
    const cached = await Promise.race([
      cachedRuleRows(getMeta),
      new Promise((resolve) => {
        timeoutId = window.setTimeout(() => resolve(cacheTimedOut), 750);
      }),
    ]);
    window.clearTimeout(timeoutId);
    if (cached === cacheTimedOut) {
      console.warn('cached business rules did not respond during startup; using offline defaults');
    } else {
      ruleRows = cached;
    }
  } catch (err) {
    // A damaged/blocked rules-cache read must never disable the sketch. The
    // compiled rules are the documented offline fallback; cloud refresh can
    // repopulate the cache after startup.
    console.error('cached business rules could not be read; using offline defaults:', err);
  }
  /**
   * Refreshes the cached business rules.
   *
   * Failures used to be swallowed entirely, which is how a device could sit on
   * the compiled-in fallback type list — no L-shelf, no base cabinets — with
   * nothing anywhere saying why (owner, 2026-08-04). Signed out or offline are
   * EXPECTED and stay quiet; anything else is reported, because the visible
   * symptom (a short section palette) looks nothing like the cause.
   */
  const refreshRules = async () => {
    // currentUser() is ASYNC — awaited, or this guard tests a Promise and is
    // always true, reporting a spurious failure on every signed-out load.
    if (!(await auth.currentUser())) return; // signed out: rules sit behind RLS
    if (typeof navigator !== 'undefined' && navigator.onLine === false) return; // offline is normal here
    try {
      await pullBusinessRules(getSupabase(), setMeta);
      reportRulesStatus(null);
    } catch (err) {
      reportRulesStatus(err);
    }
  };

  /** Surfaces a rules-pull failure where a designer will actually see it. */
  const reportRulesStatus = (err) => {
    const el = document.getElementById('sync-status');
    if (!err) {
      if (el?.dataset.rulesError) {
        delete el.dataset.rulesError;
        renderSaveSyncIndicator();
      }
      return;
    }
    console.error('business rules pull failed:', err);
    if (!el) return;
    el.dataset.rulesError = err.message ?? 'Unknown rules refresh error';
    // Deliberately a tooltip + console entry rather than a banner: the app is
    // fully usable on cached rules, so this must inform without interrupting.
    // platform-boundary-exception: the raw error text captured above is stored
    // for the indicator to render, with no retry-policy branch — see
    // platform/boundary-exceptions.json. (main interpolated it into the title
    // attribute here; this branch routes the same text through
    // deriveSaveSyncIndicator instead, so the tooltip has ONE author.)
    // Written without the literal property access on purpose: the gate's regex
    // scans prose too, so spelling it out here would count this comment as a
    // call site and inflate its own tally.
    renderSaveSyncIndicator();
  };

  // ONE library instance, shared by the app (saves, panel) and the engine
  // (push, pull-merge) — D97. app.js calls load(); the engine skips the
  // entity until that load succeeds, so an unreadable library is never
  // pushed from or merged into.
  const sectionLibrary = createReusableSectionLibrary({
    getMetaResult, setMeta, makeId: () => crypto.randomUUID(),
  });

  // Sketching never depends on a session (offline-first), so sync attaches from
  // the OUTSIDE: the engine reads/writes the same IndexedDB the app does, and
  // the app only tells it "this room changed".
  const engine = createSyncEngine({
    // The stream stamp's database half: cursors written here are only ever
    // trusted against this URL + this signed-in user (ADR 0102).
    streamKey: SUPABASE_URL,
    getSupabase,
    auth,
    db: {
      getSketch, putSketch, deleteSketch, listSketches, getMeta, setMeta,
      getRemeasureSession, putRemeasureSession, listRemeasureSessions,
      getMeasurementSession, putMeasurementSession, listMeasurementSessions, deleteMeasurementSession,
    },
    deriveProjectId,
    sketchToRows,
    rowsToSketch,
    pushRoom,
    pushRoomDelete,
    pullRooms,
    remeasureSessionToRows,
    rowToRemeasureSession,
    measurementSessionToRows,
    rowToMeasurementSession,
    pushMeasurementSession,
    pullMeasurementSessions,
    pushRemeasure,
    pullRemeasures,
    sectionLibrary,
    componentToRow,
    rowToComponent,
    pushReusableSection,
    pullReusableSections,
    onStatus: (status) => {
      renderSyncStatus(status);
      if (status.state === 'synced' && status.pulled > 0) app?.onRemoteChange?.(status);
    },
  });

  app = startApp(document, {
    sync: engine,
    ruleRows,
    auth,
    supabase: getSupabase(),
    onLocalSaveStatus: renderLocalSaveStatus,
    sectionLibrary,
  });
  wireAccountPanel(document, auth);

  // Sync once at boot and whenever the session changes (sign-in drains the
  // outbox that piled up while signed out; sign-out just goes quiet). Rules
  // refresh on the same beats.
  auth.onChange(() => {
    engine.syncNow().catch(() => {});
    refreshRules();
  });
  // Backgrounding is "closing out" in the only sense a phone has — users
  // switch apps rather than clicking Close (N1(b), Coordinator ruling
  // 2026-08-29). The flush is push-only and dirty-gated: zero requests on an
  // incidental backgrounding (a notification, a glance at another app), one
  // bounded push when there are readings to save. It races the page's own
  // freezing/discard, which is exactly why it is not a full sync.
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') engine.flushNow().catch(() => {});
  });
  engine.syncNow().catch(() => {});
  refreshRules();
}

function bootSafely() {
  boot().catch((err) => {
    console.error('Room Sketch startup failed:', err);
    const status = document.getElementById('sync-status');
    if (status) {
      const label = document.getElementById('sync-status-label');
      const message = `App startup failed — reload this page. ${err?.message ?? 'Unknown startup error'}`;
      if (label) label.textContent = message;
      status.className = 'sync-status sync-status--error';
      status.title = message;
    }
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', bootSafely);
} else {
  bootSafely();
}

// Register the offline app shell (public/sw.js, ADR 0017). After `load` so it
// never competes with first paint; the first visit installs it, every visit
// after that can cold-start with no connectivity. Registration failure is
// logged but non-fatal — the app still runs, just without offline boot.
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker
      .register('/sw.js', { updateViaCache: 'none' })
      .catch((err) => console.error('service worker registration failed:', err));
  });
}
